import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { clearError as clearChatError } from 'reduxBits/chat/actions'
import { Snackbar, IconInfo, IconWarning, colors } from '@blocket/blocket-ui'
import styled from 'styled-components'

// z-index fix to make SideBar clickable, when there is no error
const Wrapper = styled.div`
  position: ${({ position }) => position};
  z-index: ${({ isActive }) => (isActive ? 5 : -1)};
  width: 100%;
  background: transparent;
`

const Description = ({ description, displayError }) => (
  <>
    {description}
    {displayError && (
      <>
        <br />
        {displayError}
      </>
    )}
  </>
)

export const InfoSnackbar = ({ position = 'absolute' }) => {
  const [isActive, setIsActive] = useState(false)
  const [dataTestId, setDataTestId] = useState()
  const [errorType, setErrorType] = useState()
  const { type, subject, description, data, displayError } = useSelector(
    (state) => state.chat.error,
  )
  const dispatch = useDispatch()
  const animationTimerRef = useRef()
  const hideBarTimerRef = useRef()

  // since we clear the error instantly we want to preserve the style until the InfoSnackbar has faded-out
  const handleFadeOutAnimation = useCallback(() => {
    const animationLength = 200
    if (type) {
      setErrorType(type)
    }

    if (!isActive && !type) {
      animationTimerRef.current = setTimeout(() => {
        setErrorType(null)
      }, animationLength)
    }
  }, [isActive, type])

  useEffect(() => {
    if (typeof type === 'string') {
      setDataTestId(`${type}-error`)
      setIsActive(true)
      handleFadeOutAnimation()
    } else {
      setDataTestId(null)
      setIsActive(false)
    }

    return () => {
      clearTimeout(animationTimerRef.current)
    }
  }, [type, handleFadeOutAnimation])

  // timeout for displaying 'general' errors
  useEffect(() => {
    if (isActive && type !== 'offline' && type !== 'general_static') {
      hideBarTimerRef.current = setTimeout(() => {
        setIsActive(false)
        dispatch(clearChatError())
      }, 20000)
    }

    return () => {
      clearTimeout(hideBarTimerRef.current)
    }
  }, [dispatch, isActive, type])

  const onCloseBtnChange = () => {
    setIsActive(false)
    dispatch(clearChatError())
  }

  if (type === 'multiple_errors_files/images') {
    const snackBars = data.map((errorGroup, index) => {
      return (
        <Snackbar
          key={errorGroup.type}
          type="error"
          subject={errorGroup.subject + errorGroup.targets}
          description={errorGroup.description}
          active={isActive}
          icon={<IconWarning width={24} height={24} color={colors.white100} />}
          showCloseBtn={index === 0}
          onCloseBtn={onCloseBtnChange}
        />
      )
    })
    return (
      <Wrapper data-testid={dataTestId} isActive={isActive} position={position}>
        {snackBars}
      </Wrapper>
    )
  }

  return (
    <Wrapper data-testid={dataTestId} isActive={isActive} position={position}>
      <Snackbar
        type={errorType === 'offline' ? 'info' : 'error'}
        subject={subject}
        description={<Description description={description} displayError={displayError} />}
        active={isActive}
        icon={
          errorType === 'offline' ? (
            <IconInfo width={24} height={24} color={colors.blue600} />
          ) : (
            <IconWarning width={24} height={24} color={colors.white100} />
          )
        }
        showCloseBtn={errorType !== 'offline' && errorType !== 'general_static'}
        onCloseBtn={onCloseBtnChange}
      />
    </Wrapper>
  )
}

InfoSnackbar.propTypes = {
  position: PropTypes.string,
}

export default React.memo(InfoSnackbar)
